import { useCallback } from 'react';
import { useGooglePlaces, type Place, type PlaceDetails } from 'GooglePlaces';
import { TextField, type TextFieldProps } from '@mui/material';
import { locationString, formattedAddress } from 'Location';
import { Controller, useFormContext } from 'FrontRoyalReactHookForm';

type SingleLocationInputProps = {
    name: string;
    placeType: string;
} & TextFieldProps;

const locationInputOptions = (placeType: string) => {
    if (placeType === 'address') {
        return {
            googlePlaceOptions: {
                types: null,
            },
            formatter: formattedAddress,
        };
    }
    return {
        googlePlaceOptions: {
            types: ['(cities)'],
        },
        formatter: locationString,
    };
};

type LocationInputValue = { id: string; details: PlaceDetails };

export const SingleLocationInput = ({ name, placeType, ...rest }: SingleLocationInputProps) => {
    const { control, setFieldValue, watch } = useFormContext<{ [name: string]: LocationInputValue }>();
    const value = watch(name);

    const onPlaceChanged = useCallback(
        ({ place_id }: Place, placeDetails: PlaceDetails) => {
            setFieldValue(name, {
                id: place_id,
                details: placeDetails,
            });
        },
        [setFieldValue, name],
    );

    const { googlePlaceOptions, formatter } = locationInputOptions(placeType);

    const ref = useGooglePlaces({
        autoCompleteOptions: googlePlaceOptions,
        onPlaceChanged,
    });

    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <div>
                    <TextField
                        name="placesInput"
                        defaultValue={value ? formatter(value.details) : ''}
                        {...rest}
                        slotProps={{
                            htmlInput: {
                                onBlur: field.onBlur,
                            },
                        }}
                        ref={ref}
                        variant="outlined"
                    />
                </div>
            )}
        />
    );
};

export default SingleLocationInput;
